import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { FC, memo } from 'react';
import { BERTH_RESERVATION_STATUS_CONFIG } from 'src/constants/berthReservation';
import datetimeDecorator from "src/decorators/datetime.decorator";
import { LaneBerthReservation, LaneLocationSummary, ShipmentOrderLaneDetail } from 'src/entities/ShipmentOrderLane';

import { EditableBerthReservation } from '../hooks/useLaneDetailEdit';

// 表示用の予約データ型
type DisplayBerthReservation = Omit<LaneBerthReservation, 'id'> & {
  id?: number;
  tempId: string;
};

type Props = {
  detail: ShipmentOrderLaneDetail;
  isEditing?: boolean;
  berthReservations?: EditableBerthReservation[];
  onUpdateReservation?: (tempId: string, field: keyof Omit<LaneBerthReservation, 'id'>, value: string | null) => void;
  onAddReservation?: (kind: 'loading' | 'unloading') => void;
  onRemoveBerthReservation?: (tempId: string) => void;
};

type ReservationTableProps = {
  title: string;
  type: 'loading' | 'unloading';
  locationSummary: LaneLocationSummary;
  reservations: DisplayBerthReservation[];
  isEditing: boolean;
  onUpdateReservation?: (tempId: string, field: keyof Omit<LaneBerthReservation, 'id'>, value: string | null) => void;
  onAddReservation?: (kind: 'loading' | 'unloading') => void;
  onRemoveBerthReservation?: (tempId: string) => void;
};

const ReservationTable: FC<ReservationTableProps> = memo(({
  title,
  type,
  locationSummary,
  reservations,
  isEditing,
  onUpdateReservation,
  onAddReservation,
  onRemoveBerthReservation,
}) => {
  const berthReservationStatus = BERTH_RESERVATION_STATUS_CONFIG[(locationSummary?.berthReservationStatus || 'not_required') as keyof typeof BERTH_RESERVATION_STATUS_CONFIG];

  return (
    <Box sx={{ mb: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="subtitle2" gutterBottom sx={{ mb: 0 }}>
            {title}: {locationSummary.name} ({locationSummary.address})
          </Typography>
          <Chip
            size="small"
            label={berthReservationStatus.label}
            color={berthReservationStatus.color}
          />
        </Stack>
        {isEditing && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => onAddReservation(type)}
          >
            追加
          </Button>
        )}
      </Box>
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>予約番号</TableCell>
            <TableCell>予約時間</TableCell>
            <TableCell>運送会社名</TableCell>
            <TableCell>車両番号</TableCell>
            <TableCell>ドライバー名</TableCell>
            <TableCell>ドライバー電話番号</TableCell>
            {isEditing && (
              <TableCell>操作</TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {reservations.length > 0 ? (
            reservations.map((reservation) => (
                <TableRow key={reservation.tempId}>
                  <TableCell>
                    {isEditing ? (
                      <TextField
                        size="small"
                        value={reservation.reservationNumber || ''}
                        onChange={(e) => onUpdateReservation?.(reservation.tempId, 'reservationNumber', e.target.value || null)}
                        sx={{ width: 100 }}
                      />
                    ) : (
                      reservation.reservationNumber || '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <TextField
                          size="small"
                          type="time"
                          value={reservation.reservedFrom ? datetimeDecorator.toHourMinutes(new Date(reservation.reservedFrom)) : ''}
                          onChange={(e) => onUpdateReservation?.(reservation.tempId, 'reservedFrom', e.target.value || null)}
                          sx={{ width: 100 }}
                          slotProps={{ inputLabel: { shrink: true }}}
                        />
                        <Typography variant="body2">-</Typography>
                        <TextField
                          size="small"
                          type="time"
                          value={reservation.reservedTo ? datetimeDecorator.toHourMinutes(new Date(reservation.reservedTo)) : ''}
                          onChange={(e) => onUpdateReservation?.(reservation.tempId, 'reservedTo', e.target.value || null)}
                          sx={{ width: 100 }}
                          slotProps={{ inputLabel: { shrink: true }}}
                        />
                      </Box>
                    ) : (
                      reservation.reservedFrom && reservation.reservedTo
                        ? `${datetimeDecorator.toYyyyMmDdHhMm(new Date(reservation.reservedFrom))} - ${datetimeDecorator.toYyyyMmDdHhMm(new Date(reservation.reservedTo))}`
                        : '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <TextField
                        size="small"
                        value={reservation.carrierCompany || ''}
                        onChange={(e) => onUpdateReservation?.(reservation.tempId, 'carrierCompany', e.target.value || null)}
                        sx={{ width: 100 }}
                      />
                    ) : (
                      reservation.carrierCompany || '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <TextField
                        size="small"
                        value={reservation.truckNumber || ''}
                        onChange={(e) => onUpdateReservation?.(reservation.tempId, 'truckNumber', e.target.value || null)}
                        sx={{ width: 80 }}
                      />
                    ) : (
                      reservation.truckNumber || '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <TextField
                        size="small"
                        value={reservation.driverName || ''}
                        onChange={(e) => onUpdateReservation?.(reservation.tempId, 'driverName', e.target.value || null)}
                        sx={{ width: 80 }}
                      />
                    ) : (
                      reservation.driverName || '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <TextField
                        size="small"
                        value={reservation.driverContact || ''}
                        onChange={(e) => onUpdateReservation?.(reservation.tempId, 'driverContact', e.target.value || null)}
                        sx={{ width: 100 }}
                      />
                    ) : (
                      reservation.driverContact || '-'
                    )}
                  </TableCell>
                  {isEditing && (
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => onRemoveBerthReservation(reservation.tempId)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))
          ) : (
            <TableRow>
              <TableCell colSpan={isEditing ? 7 : 6} align="center">
                <Typography variant="body2" color="text.secondary">
                  予約がありません
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
    </Box>
  );
});

/**
 * レーン詳細 - バース情報タブ
 */
const LaneDetailBerthInfo: FC<Props> = memo(({
  detail,
  isEditing = false,
  berthReservations,
  onUpdateReservation,
  onAddReservation,
  onRemoveBerthReservation,
}) => {
  // 表示用データ（編集時は編集データ、それ以外は詳細データを使用）
  const displayReservations = isEditing
    ? berthReservations || []
    : (detail.laneBerthReservations || []).map((r) => ({ ...r, tempId: `display-${r.id}` }));

  // 積地・降地で予約を分類
  const loadingReservations = displayReservations.filter(
    (r) => r.kind === 'loading'
  );
  const unloadingReservations = displayReservations.filter(
    (r) => r.kind === 'unloading'
  );

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" fontWeight="bold" mb={3}>バース情報</Typography>
        <ReservationTable
          title="積地バース情報"
          type="loading"
          locationSummary={detail.loadingLocation}
          reservations={loadingReservations}
          isEditing={isEditing}
          onUpdateReservation={onUpdateReservation}
          onAddReservation={onAddReservation}
          onRemoveBerthReservation={onRemoveBerthReservation}
        />
        <ReservationTable
          title="降地バース情報"
          type="unloading"
          locationSummary={detail.unloadingLocation}
          reservations={unloadingReservations}
          isEditing={isEditing}
          onUpdateReservation={onUpdateReservation}
          onAddReservation={onAddReservation}
          onRemoveBerthReservation={onRemoveBerthReservation}
        />
      </CardContent>
    </Card>
  );
});

export default LaneDetailBerthInfo;
